/**
 * Theme palette + color scheme — see DESIGN.md §3.4 / §3.4.1.
 *
 * Two independent axes:
 *   • Palette (Apricot, Plum, Sage, Lagoon, Ink) → `data-theme`
 *   • Color scheme (auto / light / dark)         → `data-scheme`
 *
 * Both persist via localStorage (instant client reads) AND a cookie
 * (so the server-rendered <html> can carry both attributes on the
 * first paint, avoiding a flash of the wrong palette/scheme).
 */

// ─────────────────────────────────────────────────────────────────
//  Palette
// ─────────────────────────────────────────────────────────────────

export const PALETTES = ['apricot', 'plum', 'sage', 'lagoon', 'ink'] as const;
export type Palette = (typeof PALETTES)[number];

export const DEFAULT_PALETTE: Palette = 'lagoon';

const PALETTE_STORAGE_KEY = 'flowlet:theme';
const PALETTE_COOKIE_KEY = 'flowlet_theme';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

export function isPalette(value: unknown): value is Palette {
  return typeof value === 'string' && (PALETTES as readonly string[]).includes(value);
}

export function getTheme(): Palette {
  if (typeof window === 'undefined') return DEFAULT_PALETTE;
  const stored = window.localStorage.getItem(PALETTE_STORAGE_KEY);
  return isPalette(stored) ? stored : DEFAULT_PALETTE;
}

export function setTheme(palette: Palette): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(PALETTE_STORAGE_KEY, palette);
  document.cookie = `${PALETTE_COOKIE_KEY}=${palette}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
  document.documentElement.dataset.theme = palette;
  for (const cb of paletteListeners) cb();
}

// ─────────────────────────────────────────────────────────────────
//  Color scheme (light / dark / auto)
// ─────────────────────────────────────────────────────────────────

export const SCHEMES = ['auto', 'light', 'dark'] as const;
export type Scheme = (typeof SCHEMES)[number];

export const DEFAULT_SCHEME: Scheme = 'dark';

const SCHEME_STORAGE_KEY = 'flowlet:scheme';
const SCHEME_COOKIE_KEY = 'flowlet_scheme';

export function isScheme(value: unknown): value is Scheme {
  return typeof value === 'string' && (SCHEMES as readonly string[]).includes(value);
}

export function getScheme(): Scheme {
  if (typeof window === 'undefined') return DEFAULT_SCHEME;
  const stored = window.localStorage.getItem(SCHEME_STORAGE_KEY);
  return isScheme(stored) ? stored : DEFAULT_SCHEME;
}

/**
 * Set the active color scheme. `auto` clears the manual override so the
 * OS preference takes over. Mirrors palette persistence (cookie + storage)
 * and updates the `<html data-scheme>` attribute synchronously.
 */
export function setScheme(scheme: Scheme): void {
  if (typeof window === 'undefined') return;
  if (scheme === 'auto') {
    window.localStorage.removeItem(SCHEME_STORAGE_KEY);
    document.cookie = `${SCHEME_COOKIE_KEY}=; path=/; max-age=0; SameSite=Lax`;
    delete document.documentElement.dataset.scheme;
  } else {
    window.localStorage.setItem(SCHEME_STORAGE_KEY, scheme);
    document.cookie = `${SCHEME_COOKIE_KEY}=${scheme}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
    document.documentElement.dataset.scheme = scheme;
  }
  for (const cb of schemeListeners) cb();
}

// ─────────────────────────────────────────────────────────────────
//  Subscriptions (useSyncExternalStore plumbing)
// ─────────────────────────────────────────────────────────────────

type Listener = () => void;
const paletteListeners = new Set<Listener>();
const schemeListeners = new Set<Listener>();

export function subscribeTheme(cb: Listener): () => void {
  if (typeof window === 'undefined') return () => {};
  paletteListeners.add(cb);
  const onStorage = (e: StorageEvent) => {
    if (e.key === PALETTE_STORAGE_KEY) cb();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    paletteListeners.delete(cb);
    window.removeEventListener('storage', onStorage);
  };
}

export function subscribeScheme(cb: Listener): () => void {
  if (typeof window === 'undefined') return () => {};
  schemeListeners.add(cb);
  const onStorage = (e: StorageEvent) => {
    if (e.key === SCHEME_STORAGE_KEY) cb();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    schemeListeners.delete(cb);
    window.removeEventListener('storage', onStorage);
  };
}

export function getServerSnapshot(): Palette {
  return DEFAULT_PALETTE;
}

export function getServerSchemeSnapshot(): Scheme {
  return DEFAULT_SCHEME;
}

// ─────────────────────────────────────────────────────────────────
//  Server-side helpers (reading cookies during SSR)
// ─────────────────────────────────────────────────────────────────

export function paletteFromCookieHeader(cookieHeader: string | null | undefined): Palette {
  if (!cookieHeader) return DEFAULT_PALETTE;
  for (const part of cookieHeader.split(';')) {
    const [k, v] = part.trim().split('=');
    if (k === PALETTE_COOKIE_KEY && isPalette(v)) return v;
  }
  return DEFAULT_PALETTE;
}

export function schemeFromCookieHeader(cookieHeader: string | null | undefined): Scheme {
  if (!cookieHeader) return DEFAULT_SCHEME;
  for (const part of cookieHeader.split(';')) {
    const [k, v] = part.trim().split('=');
    if (k === SCHEME_COOKIE_KEY && isScheme(v)) return v;
  }
  return DEFAULT_SCHEME;
}

// ─────────────────────────────────────────────────────────────────
//  Display data (palette swatches / labels)
// ─────────────────────────────────────────────────────────────────

export const PALETTE_LABELS: Record<Palette, string> = {
  apricot: 'Apricot',
  plum: 'Plum',
  sage: 'Sage',
  lagoon: 'Lagoon',
  ink: 'Ink',
};

/**
 * Hex pair for the swatch preview — light-mode start/end, matches DESIGN.md §3.4.
 * Swatches always show the light-mode gradient even in dark mode so the
 * hue identity stays consistent across the picker.
 */
export const PALETTE_SWATCH: Record<Palette, [string, string]> = {
  apricot: ['#F97316', '#E11D48'],
  plum: ['#9333EA', '#DB2777'],
  sage: ['#4D7C0F', '#84CC16'],
  lagoon: ['#0E7490', '#4338CA'],
  ink: ['#1A130B', '#44403C'],
};

export const SCHEME_LABELS: Record<Scheme, string> = {
  auto: 'Auto',
  light: 'Light',
  dark: 'Dark',
};
